/**
 * Shared onPreSave helpers for publishable collections.
 */
import {
  assertCompleteForReady,
  type CompletenessCollector,
} from "./completenessValidation";
import {
  assertStatusTransition,
  normalizeContentStatus,
  type ContentStatus,
} from "./contentStatus";
import {
  contentMeaningfullyChanged,
  hasPublishedSnapshot,
} from "./publishedSnapshot";

export function createStatusPreSave(options: {
  collectCompleteness: CompletenessCollector;
  entityLabel: string;
}): (args: {
  values: Record<string, unknown>;
  previousValues?: Record<string, unknown>;
}) => Record<string, unknown> {
  const { collectCompleteness, entityLabel } = options;

  return ({ values, previousValues }) => {
    const prev = normalizeContentStatus(previousValues?.status);
    let next = normalizeContentStatus(values.status);

    // Preserve engine/publish-written snapshot if the form did not load it.
    if (
      hasPublishedSnapshot(previousValues) &&
      !hasPublishedSnapshot(values)
    ) {
      values.published_snapshot = previousValues!.published_snapshot;
    }

    // Auto: once live, any unpublished edit becomes Live · Draft.
    const liveWorking: ContentStatus[] = ["published", "published_draft", "published_ready"];
    if (liveWorking.includes(prev)) {
      const leavingLiveWorkflow =
        next === "unpublishing" || next === "archived";
      if (!leavingLiveWorkflow) {
        const edited = contentMeaningfullyChanged(previousValues, values);
        if (prev === "published" && (edited || next === "published")) {
          next = next === "published_ready" ? "published_ready" : "published_draft";
          values.status = next;
        } else if (
          (prev === "published_ready" || prev === "published_draft") &&
          edited &&
          next === prev
        ) {
          // Edits while queued/ready drop back to Live · Draft so Publish isn't stale.
          if (prev === "published_ready") {
            next = "published_draft";
            values.status = next;
          }
        } else if (prev === "published" && next === "published_draft") {
          values.status = next;
        }
      }
    }

    assertStatusTransition(prev, next);

    if (
      (next === "ready_for_publishing" && prev !== "ready_for_publishing") ||
      (next === "published_ready" && prev !== "published_ready")
    ) {
      assertCompleteForReady(values, collectCompleteness, entityLabel);
    }

    return values;
  };
}
